import { useCallback, useEffect, useState } from 'react';
import {
  brandportfolioApi,
  type CannibalizationRecord,
  type InnovationIdea,
  type LifecycleRecord,
  type PortfolioDashboard,
  type PortfolioItem,
  type PortfolioRecommendation,
  type PortfolioScenario,
  type ResourceAllocation,
} from '../../api/brandportfolio';

type Tab = 'overview' | 'portfolio' | 'lifecycle' | 'innovation' | 'cannibalization' | 'scenarios' | 'allocation' | 'recommendations';

function stageClass(stage: string) {
  if (stage === 'growth' || stage === 'launch') return 'text-emerald-700 bg-emerald-50';
  if (stage === 'maturity') return 'text-sky-700 bg-sky-50';
  if (stage === 'decline' || stage === 'retirement') return 'text-red-700 bg-red-50';
  if (stage === 'research' || stage === 'development' || stage === 'idea') return 'text-violet-700 bg-violet-50';
  return 'text-slate-600 bg-slate-50';
}

function healthClass(score: number) {
  if (score >= 80) return 'text-emerald-700';
  if (score >= 60) return 'text-amber-700';
  return 'text-red-700';
}

export function BrandPortfolioPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<PortfolioDashboard | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>([]);
  const [lifecycle, setLifecycle] = useState<LifecycleRecord[]>([]);
  const [innovation, setInnovation] = useState<InnovationIdea[]>([]);
  const [cannibalization, setCannibalization] = useState<CannibalizationRecord[]>([]);
  const [scenarios, setScenarios] = useState<PortfolioScenario[]>([]);
  const [allocation, setAllocation] = useState<ResourceAllocation[]>([]);
  const [recommendations, setRecommendations] = useState<PortfolioRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, port, lc, inn, can, sc, alloc, recs] = await Promise.all([
        brandportfolioApi.dashboard(),
        brandportfolioApi.listPortfolio(),
        brandportfolioApi.listLifecycle(),
        brandportfolioApi.listInnovation(),
        brandportfolioApi.listCannibalization(),
        brandportfolioApi.listScenarios(),
        brandportfolioApi.listAllocation(),
        brandportfolioApi.listRecommendations(),
      ]);
      setDashboard(dash);
      setPortfolio(port);
      setLifecycle(lc);
      setInnovation(inn);
      setCannibalization(can);
      setScenarios(sc);
      setAllocation(alloc);
      setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load brand portfolio data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await brandportfolioApi.generateRecommendations(); await refresh(); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Portfolio Dashboard' },
    { id: 'portfolio', label: 'Portfolio Overview' },
    { id: 'lifecycle', label: 'Lifecycle Explorer' },
    { id: 'innovation', label: 'Innovation Center' },
    { id: 'cannibalization', label: 'Cannibalization' },
    { id: 'scenarios', label: 'Scenario Simulator' },
    { id: 'allocation', label: 'Resource Allocation' },
    { id: 'recommendations', label: 'Recommendation Workspace' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Brand Portfolio Intelligence</h1>
          <p className="text-sm text-slate-500">Portfolio lifecycle, innovation &amp; brand strategy — BCP-00009</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Total SKUs', value: dashboard.total_skus.toString() },
            { label: 'Collections', value: dashboard.total_collections.toString() },
            { label: 'Portfolio Growth', value: `${dashboard.portfolio_growth_pct.toFixed(1)}%` },
            { label: 'Innovation Index', value: dashboard.innovation_index.toFixed(0) },
            { label: 'Lifecycle Balance', value: `${dashboard.lifecycle_balance_score.toFixed(0)}%` },
            { label: 'Hero Products', value: dashboard.hero_product_count.toString() },
            { label: 'Declining Products', value: dashboard.declining_products.toString() },
            { label: 'Cannibalization Issues', value: dashboard.cannibalization_issues.toString() },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'portfolio' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">SKUs</th>
                <th className="px-4 py-2">Revenue %</th>
                <th className="px-4 py-2">Growth</th>
                <th className="px-4 py-2">Stage</th>
                <th className="px-4 py-2">Health</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {portfolio.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-2 font-medium">{p.name}</td>
                  <td className="px-4 py-2 capitalize text-slate-600">{p.item_type.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-2">{p.sku_count}</td>
                  <td className="px-4 py-2">{p.revenue_pct.toFixed(0)}%</td>
                  <td className="px-4 py-2">{p.growth_rate_pct > 0 ? '+' : ''}{p.growth_rate_pct.toFixed(1)}%</td>
                  <td className="px-4 py-2">
                    {p.lifecycle_stage && (
                      <span className={`rounded px-2 py-0.5 text-xs capitalize ${stageClass(p.lifecycle_stage)}`}>{p.lifecycle_stage}</span>
                    )}
                  </td>
                  <td className={`px-4 py-2 font-medium ${healthClass(p.health_score)}`}>{p.health_score.toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'lifecycle' && (
        <div className="space-y-3">
          {lifecycle.map((l) => (
            <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <h3 className="font-medium text-slate-900">{l.product_name}</h3>
                  <p className="text-sm text-slate-500">{l.sku ?? 'Pipeline'} · {l.collection}</p>
                  {l.recommended_action && (
                    <p className="text-sm text-sky-600 mt-1 capitalize">→ {l.recommended_action.replace(/_/g, ' ')}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <span className={`rounded px-2 py-0.5 text-xs capitalize ${stageClass(l.current_stage)}`}>{l.current_stage}</span>
                  <p className="text-xs text-slate-400 mt-1">{l.days_in_stage} days in stage</p>
                  {l.revenue_trend && <p className="text-xs capitalize">Trend: {l.revenue_trend}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'innovation' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {innovation.map((i) => (
            <div key={i.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start">
                <h3 className="font-medium text-slate-900">{i.title}</h3>
                <span className="text-xs rounded bg-violet-50 text-violet-700 px-2 py-0.5 capitalize">{i.status}</span>
              </div>
              {i.description && <p className="text-sm text-slate-600 mt-1">{i.description}</p>}
              {i.evidence && <p className="text-xs text-slate-400 mt-2">{i.evidence}</p>}
              <p className="text-sm mt-2">
                <span className="text-emerald-600 font-medium">+{i.revenue_potential_pct}%</span> potential · Risk {(i.risk_score * 100).toFixed(0)}%
              </p>
              {i.source_modules && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {i.source_modules.map((m) => (
                    <span key={m} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{m}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'cannibalization' && (
        <div className="space-y-4">
          {cannibalization.map((c) => (
            <div key={c.id} className="rounded-xl border border-amber-200 bg-amber-50/30 p-4 shadow-sm">
              <span className="text-xs font-medium uppercase text-amber-700">{c.conflict_type.replace(/_/g, ' ')}</span>
              <div className="mt-2 grid sm:grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-slate-400">Product A</p><p>{c.product_a}</p></div>
                <div><p className="text-xs text-slate-400">Product B</p><p>{c.product_b}</p></div>
              </div>
              <p className="text-sm mt-2">Overlap {(c.overlap_score * 100).toFixed(0)}% · Revenue impact {c.revenue_impact_pct}%</p>
              {c.recommendation && (
                <p className="text-sm text-emerald-700 mt-2 border-t border-amber-100 pt-2">{c.recommendation}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'scenarios' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {scenarios.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{s.name}</h3>
              <p className="text-xs text-slate-500 capitalize mt-1">{s.type.replace(/_/g, ' ')}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div><p className="text-emerald-600 font-medium">{s.revenue_impact_pct > 0 ? '+' : ''}{s.revenue_impact_pct}%</p><p className="text-xs text-slate-400">Revenue</p></div>
                <div><p className="text-sky-600 font-medium">+{s.profit_impact_pct}%</p><p className="text-xs text-slate-400">Profit</p></div>
              </div>
              <p className="text-xs text-slate-500 mt-2">{s.skus_affected} SKUs · Risk {(s.risk_score * 100).toFixed(0)}%</p>
              {s.description && <p className="text-xs text-slate-600 mt-1">{s.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'allocation' && (
        <div className="space-y-4">
          {allocation.map((a) => (
            <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{a.collection ?? 'Portfolio-wide'}</h3>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                {[
                  { label: 'Ad Budget', value: a.ad_budget_pct },
                  { label: 'Content', value: a.content_investment_pct },
                  { label: 'Inventory', value: a.inventory_investment_pct },
                  { label: 'Innovation', value: a.innovation_budget_pct },
                  { label: 'AI Credits', value: a.ai_credits_pct },
                ].map((m) => (
                  <div key={m.label}>
                    <p className="text-slate-500 text-xs">{m.label}</p>
                    <p className="font-medium">{m.value.toFixed(0)}%</p>
                  </div>
                ))}
              </div>
              {a.rationale && <p className="text-xs text-slate-500 mt-2">{a.rationale}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" onClick={generateRecs} disabled={busy}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
              Generate Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <span className="text-xs font-medium uppercase text-sky-600">#{r.priority_rank} · {r.type}</span>
                  <h3 className="font-medium text-slate-900 mt-1">{r.title}</h3>
                  {r.description && <p className="text-sm text-slate-600 mt-1">{r.description}</p>}
                  {r.evidence && <p className="text-xs text-slate-400 mt-1">{r.evidence}</p>}
                  {r.expected_impact && <p className="text-xs text-emerald-600 mt-1">{r.expected_impact}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold">{(r.confidence_score * 100).toFixed(0)}%</p>
                  {r.requires_approval && (
                    <button type="button" onClick={() => brandportfolioApi.approveRecommendation(r.id).then(refresh)}
                      className="mt-2 rounded bg-sky-100 px-3 py-1 text-xs font-medium text-sky-700 hover:bg-sky-200">
                      Approve
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
